/**
 * Adapptio Editor
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { v4 as uuid_v4 } from "uuid";

import { createEventEmitter, emitEvent } from "@adapptio/lib-shared";
import { Dialog } from "../../../dialogs/Dialog";
import { IEditor } from "../../../IEditor";
import { ResourceService } from "../../../resources/ResourceService";
import { UploadAssetDialogComponent } from "./UploadAssetDialogComponent";
import { ToastService } from "../../../toasts/ToastService";
import { TOAST_TYPE } from "../../../toasts/IToast";
import { ErrorTracker } from "@adapptio/lib-components";

export interface UploadAssetDialog_Params {
	assetId?: string;
	basePath?: string;
}

export interface UploadAssetDialog_Result {
	resourceId: string;
}

export interface IUploadAssetDialog_CreateParams {
	basePath: string;
	name: string;
	file: File;
}

export class UploadAssetDialog extends Dialog<UploadAssetDialog_Params, UploadAssetDialog_Result> {
	private resourceSvc: ResourceService;
	private toastSvc: ToastService;

	private assetId: string;

	private resourceId: string;

	private pending = false;

	public onPendingChange = createEventEmitter<void>();

	public constructor(dc: IEditor, id: string, params: UploadAssetDialog_Params) {
		super(dc, id, params);

		this.resourceSvc = this.dc.get("resourceService");
		this.toastSvc = dc.get("toastService");

		this.assetId = params.assetId;

		this.resourceId = uuid_v4();
	}

	public getComponent(): typeof UploadAssetDialogComponent {
		return UploadAssetDialogComponent;
	}

	public isUpdate(): boolean {
		return !!this.assetId;
	}

	/**
	 * Get a resource node for select folders when import resource
	 */
	public getParentResourceNode() {
		if (this.isUpdate()) {
			const resource = this.resourceSvc.getResourceById(this.assetId);

			const basePath = resource?.descriptor?.basePath;

			if (!basePath) {
				return null;
			}

			const parent = this.resourceSvc.getResourceByPath(basePath);

			if (!parent) {
				return null;
			}

			const resourceNode = {
				id: parent.descriptor.id,
				name: parent.descriptor.name,
				resourceType: parent.descriptor.resourceType,
				basePath: parent.descriptor.basePath,
				isOpened: true,
				canWrite: parent.descriptor.attributes.allowWrite,
				canRename: parent.descriptor.attributes.allowRename,
				canDelete: parent.descriptor.attributes.allowRemove,
				isValid: parent.descriptor.parseData.valid,
				children: []
			};

			return resourceNode;
		}

		return null;
	}

	public async upload(basePath: string, files: FileList): Promise<void> {
		if (this.pending) {
			return;
		}

		if (!files?.length) {
			return;
		}

		this.setPending(true);

		try {
			if (this.isUpdate()) {
				await this.resourceSvc.updateAsset(this.assetId, files);
			} else {
				await this.resourceSvc.createAsset(basePath, files);
			}

			this.toastSvc.add({
				type: TOAST_TYPE.SUCCESS,
				// @todo Translate
				message: "Assets have been uploaded.",
				timeoutMs: 2500
			});

			this.close({
				resourceId: this.resourceId
			});
		} catch (err) {
			const errorTracker = ErrorTracker.getInstance();
			errorTracker.captureException(err);

			this.toastSvc.add({
				type: TOAST_TYPE.ERROR,
				// @todo Translate
				message: "Failed to upload assets. Please try it again in a while.",
				details: String(err)
			});
		} finally {
			this.setPending(false);
		}
	}

	private setPending(pending: boolean): void {
		this.pending = pending;
		emitEvent(this.onPendingChange);
	}

	public isPending(): boolean {
		return this.pending;
	}
}
